import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import { useAuth } from '../../contexts/AuthContext';

function PerfilViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('config.perfis');

  const [perfil, setPerfil] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    let aborted = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [perfilResponse, perfilPermissionsResponse, permissionsCatalogResponse] = await Promise.all([
          api.get(`/perfis/${id}`),
          api.get(`/perfis/${id}/permissions`),
          api.get('/permissions'),
        ]);

        if (aborted) return;

        const perfilData = perfilResponse?.data?.data ?? perfilResponse?.data;
        const permissionNames = perfilPermissionsResponse?.data?.data?.permissions || [];
        const permissionsCatalog = permissionsCatalogResponse?.data?.data ?? permissionsCatalogResponse?.data ?? [];

        const permissionsMap = {};
        if (Array.isArray(permissionsCatalog)) {
          permissionsCatalog.forEach((item) => {
            permissionsMap[item.name] = item.description || item.name;
          });
        }

        const mappedPermissions = Array.isArray(permissionNames)
          ? permissionNames.map((permissionName) => ({
              key: permissionName,
              label: permissionsMap[permissionName] || permissionName,
            }))
          : [];

        setPerfil(perfilData);
        setPermissions(mappedPermissions);
      } catch (err) {
        if (aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      aborted = true;
    };
  }, [id, canManage]);

  if (loading) {
    return (
      <div className="perfis-form-page">
        <LoadingSpinner text="Carregando perfil..." />
      </div>
    );
  }

  if (!canManage || error?.kind === 'permission') {
    return (
      <div className="perfis-form-page">
        <PermissionDeniedAlert
          requiredPermission="config.perfis"
          action="visualizar perfis"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="perfis-form-page">
        <AlertException error={error} fallback="Não foi possível carregar o perfil." />
      </div>
    );
  }

  return (
    <div className="perfis-form-page">
      <div className="content-header">
        <h1 className="content-title">Visualizar Perfil</h1>
        <h4 className="content-subtitle">{perfil?.nome || `Perfil #${id}`}</h4>
      </div>

      <div className="card perfis-form-card">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Nome do Perfil</label>
              <h3 className="form-label">{perfil?.nome || ''}</h3>              
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Tipo de Acesso</label>
              <h3 className="form-label">{perfil?.tipo_acesso || '-'}</h3>              
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Permissões Associadas ao Perfil</label>
            <div className="p-3 rounded border" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              {permissions.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {permissions.map((permission) => (
                    <span key={permission.key} className="badge bg-secondary">
                      {permission.label}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted">Nenhuma permissão configurada.</span>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/painel/perfis')}
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PerfilViewPage;
